import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Shimmer from './Shimmer';

// Per-section shimmers for the About page. About is mostly static copy
// (differentiators, accreditations, infra, trustian, careers etc) so we
// only skeleton the sections that actually fetch data:
//   • Hero stats row         → useListAboutNumbersQuery (filtered)
//   • Our-Story timeline     → useListAboutTimelineQuery
//   • By-The-Numbers grid    → useListAboutNumbersQuery
//   • Leadership team grid   → useListAboutTeamQuery
// Contact channels have inline fallbacks so they render immediately.

// Hero: 4 big numbers on forest-green bg, translucent white shimmers so
// they stay visible against the dark section.
export const AboutHeroStatsShimmer = ({ count = 4 }) => (
  <Stack direction="row" spacing={{ xs: 4, sm: 6, md: 8 }} useFlexGap flexWrap="wrap" sx={{ rowGap: 3 }}>
    {Array.from({ length: count }).map((_, i) => (
      <Box key={i}>
        <Shimmer width={90} height={38} rounded={6} sx={{ bgcolor: 'rgba(200,237,216,0.18)', mb: 1 }} />
        <Shimmer width={120} height={10} rounded={4} sx={{ bgcolor: 'rgba(255,255,255,0.10)' }} />
      </Box>
    ))}
  </Stack>
);

// Vertical timeline. Ring marker sits on the left border, year/title/desc
// stack to the right — mirrors the real TimelineItem layout 1:1.
export const AboutTimelineShimmer = ({ count = 4 }) => (
  <Box sx={{ position: 'relative', pl: { xs: 3, md: 3.5 }, borderLeft: '2px solid var(--sage-p)', mt: { xs: 0, md: 1 } }}>
    {Array.from({ length: count }).map((_, i) => (
      <Box key={i} sx={{ position: 'relative', mb: i === count - 1 ? 0 : { xs: 4, md: 4.5 } }}>
        <Box
          sx={{
            position: 'absolute',
            left: { xs: '-34px', md: '-38px' },
            top: '2px',
            width: 18,
            height: 18,
            bgcolor: '#fff',
            border: '2.5px solid var(--sage)',
            borderRadius: '50%',
            boxSizing: 'border-box',
          }}
        />
        <Shimmer width={50} height={12} rounded={4} sx={{ mb: 1 }} />
        <Shimmer width="80%" height={14} rounded={4} sx={{ mb: 1.25 }} />
        <Shimmer width="100%" height={11} rounded={4} sx={{ mb: 0.5 }} />
        <Shimmer width="65%" height={11} rounded={4} />
      </Box>
    ))}
  </Box>
);

// By-The-Numbers grid. Matches the 2×4 → 4×2 NumberCard layout.
export const AboutNumbersShimmer = ({ count = 8 }) => (
  <Grid container spacing={{ xs: 2, md: 2.5 }} sx={{ mt: { xs: 3, md: 4 } }}>
    {Array.from({ length: count }).map((_, i) => (
      <Grid key={i} item xs={6} md={3}>
        <Box
          sx={{
            bgcolor: '#fff',
            border: '1px solid var(--border-l)',
            borderRadius: '18px',
            p: { xs: 3, md: 3.5 },
            textAlign: 'center',
            height: '100%',
          }}
        >
          <Shimmer width={110} height={44} rounded={6} sx={{ mx: 'auto', mb: 1.25 }} />
          <Shimmer width="70%" height={12} rounded={4} sx={{ mx: 'auto', mb: 0.75 }} />
          <Shimmer width="85%" height={10} rounded={4} sx={{ mx: 'auto' }} />
        </Box>
      </Grid>
    ))}
  </Grid>
);

// Leadership team. Circular avatar + name + role + bio — 4 per row on md.
export const AboutTeamShimmer = ({ count = 4 }) => (
  <Grid container spacing={{ xs: 2, md: 2.5 }} sx={{ mt: { xs: 3, md: 4 } }}>
    {Array.from({ length: count }).map((_, i) => (
      <Grid key={i} item xs={12} sm={6} md={3}>
        <Box
          sx={{
            bgcolor: '#fff',
            border: '1.5px solid var(--border-l)',
            borderRadius: '18px',
            p: 3,
            height: '100%',
            textAlign: 'center',
          }}
        >
          <Shimmer width={80} height={80} circular sx={{ mx: 'auto', mb: 2.5 }} />
          <Shimmer width="60%" height={14} rounded={4} sx={{ mx: 'auto', mb: 1 }} />
          <Shimmer width="45%" height={10} rounded={4} sx={{ mx: 'auto', mb: 1.5 }} />
          <Shimmer width="95%" height={11} rounded={4} sx={{ mx: 'auto', mb: 0.5 }} />
          <Shimmer width="80%" height={11} rounded={4} sx={{ mx: 'auto' }} />
        </Box>
      </Grid>
    ))}
  </Grid>
);
